import { AnimatedButton } from '@dtx-company/shared-components/src/components/atoms/AnimatedButton/index'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { BoxProps } from '@dtx-company/shared-components/src'
import { CheckBox } from '@dtx-company/shared-components/src/components/atoms/Checkbox/index'
import { Controller } from 'react-hook-form'
import { Disclaimer } from '../AuthForm/Disclaimer'
import { FormToggleCTA } from '../AuthForm/SignupFormToggle'
import { FormValues } from '@app/code/src/types/auth-form.types'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SSORedirect } from '@dtx-company/inter-app/src/components/singleSignOn/SSORedirect'
import { SingleSignOnEnabled } from '@dtx-company/inter-app/src/components/singleSignOn/auth-form'
import { SocialAuth } from '../../components/molecules/social-auth'
import { SocialAuthProps } from '../molecules/social-auth/index.types'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextDivider } from '../../containers/upgrade/Upgrade.styles'
import { VerticalSpacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import {
  auth_mobilelocg_signin_emailpassword_started,
  auth_mobilelocg_signin_sso_started,
  auth_mobilelocg_signup_emailpassword_started
} from '@dtx-company/inter-app/src/constants/authHeapEvents'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useAuthCache } from '@app/authentication/AuthCache/hooks'
import { useBuildCreateCodePayload } from '../Homepage/CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import { useRouter } from 'next/router'
import { useUpdateAuthFormState } from './UpgradeAuthForm.hooks'
import Link from 'next/link'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'
import isEmail from 'validator/lib/isEmail'
import useTranslation from 'next-translate/useTranslation'

// TODO - create distinct mobile + desktop layout, remove conditional logic once page is removed - DF
interface ForgotPasswordLinkProps {
  onForgotPasswordClick?: () => void
}

const ForgotPasswordLink: React.FC<ForgotPasswordLinkProps> = props => (
  <Box width="100%" justifyContent="flex-end" onClick={props.onForgotPasswordClick}>
    <Link href={Routes.FORGOT_PASSWORD} passHref legacyBehavior>
      <Text as="a" fontSize="inherit" color="primary.flowBlue" cursor="pointer">
        Forgot password?
      </Text>
    </Link>
  </Box>
)

export type UpgradeAuthFormProps = {
  onAuthFormToggle: () => void
  onSuccess?: (email?: string | null) => void
  isMobile: boolean
  isSignUp: boolean
  styles?: {
    form?: React.CSSProperties
    socialAuth?: BoxProps
  }
  onForgotPasswordClick?: () => void
  onClickSubmitBtn?: () => void
} & Pick<SocialAuthProps, 'onClickFacebookBtn' | 'onClickGoogleBtn' | 'showFacebookBtn'>

type SocialLoginsProps = Omit<UpgradeAuthFormProps, 'onAuthFormToggle'>

export function SocialLogins({
  isSignUp,
  onSuccess,
  showFacebookBtn = false,
  ...rest
}: SocialLoginsProps): JSX.Element {
  return (
    <Box
      width="100%"
      alignItems="center"
      alignSelf="center"
      flexDirection="column"
      maxWidth="440px"
    >
      <SocialAuth
        width="100%"
        onClick={() => onSuccess?.()}
        cta={`${isSignUp ? 'Sign up' : 'Sign in'} with`}
        isSignup={isSignUp}
        showFacebookBtn={showFacebookBtn}
        {...rest}
      />
    </Box>
  )
}
const UpgradeAuthForm: React.FC<UpgradeAuthFormProps> = ({
  isSignUp,
  onAuthFormToggle,
  onSuccess,
  isMobile,
  styles,
  onForgotPasswordClick,
  onClickSubmitBtn,
  onClickFacebookBtn,
  onClickGoogleBtn,
  showFacebookBtn
}) => {
  const {
    submitButtonLabel,
    checkForValidEmail,
    isAuthenticating,
    authenticateUser,
    checkIfEmailSSOEnabled,
    emailIsSSOEnabled,
    ssoRedirectHtml,
    startSSORedirect,
    handleSubmit,
    control,
    error,
    requireEmailOptIn
  } = useUpdateAuthFormState({ isSignUp, onSuccess })
  const { t } = useTranslation('auth')
  const SocialLoginDivider = (): JSX.Element => {
    return <TextDivider vertical={!isMobile} copy="OR" />
  }

  const locgFormValues = useBuildCreateCodePayload()
  const queryParams = useRouter().query
  const authCache = useAuthCache()

  const onSubmit = async (data: FormValues): Promise<void> => {
    if (emailIsSSOEnabled) {
      fireAnalyticsEvent(auth_mobilelocg_signin_sso_started)
      await Promise.all([
        authCache?.queryParams.set(queryParams),
        authCache?.locgFormValues.set(locgFormValues)
      ])
      await startSSORedirect()
    } else {
      if (isSignUp) {
        fireAnalyticsEvent(auth_mobilelocg_signup_emailpassword_started)
      } else {
        fireAnalyticsEvent(auth_mobilelocg_signin_emailpassword_started)
      }
      await authenticateUser(data)
    }
  }

  return (
    <>
      <SSORedirect redirectFormHtml={ssoRedirectHtml} />
      <Box flexDirection={isMobile ? 'column' : 'row'} width="100%" justifyContent="center">
        <form
          style={{ alignSelf: 'center', maxWidth: '343px', width: '100%', ...styles?.form }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <SocialLogins
            isSignUp={isSignUp}
            isMobile={isMobile}
            onClickFacebookBtn={onClickFacebookBtn}
            onClickGoogleBtn={onClickGoogleBtn}
            onSuccess={onSuccess}
            showFacebookBtn={showFacebookBtn}
          />
          <SocialLoginDivider />
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, ref, value, ...rest }, fieldState: { error } }) => (
              <Input
                type="email"
                label="Email"
                placeholder="your@email.com"
                helperText={error?.type === 'required' ? 'Email is required' : error?.message}
                error={Boolean(error)}
                inputProps={{
                  ...rest,
                  onFocus: events.userFocusedEmailInput,
                  'data-testid': 'upgrade-auth-form-email-input'
                }}
                id="upgrade-auth-form-email-input"
                value={value}
                onChange={e => {
                  const emailAddress = e.target?.value
                  const isEmailValid = isEmail(emailAddress)
                  checkIfEmailSSOEnabled(e.target.value)
                  checkForValidEmail(isEmailValid)
                  onChange(e)
                }}
                ref={ref}
                labelProps={{
                  marginBottom: '9px'
                }}
                width="100%"
                maxWidth="auto"
              />
            )}
          />
          <VerticalSpacer mt="16px" />
          {emailIsSSOEnabled ? (
            <>
              <SingleSignOnEnabled />
            </>
          ) : (
            <>
              <Controller
                control={control}
                name="password"
                render={({ field: { ref, value, ...rest }, fieldState: { error } }) => (
                  <Input
                    type="password"
                    placeholder={t('PasswordField.Placeholder')}
                    helperText={
                      error?.type === 'required'
                        ? t('PasswordField.Errors.Required')
                        : error?.message
                    }
                    error={Boolean(error)}
                    inputProps={{
                      ...rest,
                      onFocus: events.userFocusedPasswordInput,
                      'data-testid': 'upgrade-auth-form-password-input'
                    }}
                    ref={ref}
                    label="Password"
                    labelProps={{
                      marginBottom: '9px'
                    }}
                    value={value}
                    width="100%"
                    maxWidth="auto"
                  />
                )}
              />
              <VerticalSpacer mt="16px" />
            </>
          )}
          {isSignUp ? (
            <Box>
              <Controller
                control={control}
                name="marketingOptIn"
                render={({ field: { ref, value, ...rest } }) => (
                  <CheckBox
                    labelProps={{
                      color: 'secondary.disabled'
                    }}
                    textVariant="checkbox/small"
                    checked={value}
                    inputProps={{
                      ref
                    }}
                    {...rest}
                    label={
                      requireEmailOptIn
                        ? 'I want to receive marketing emails from Flowcode.'
                        : 'I do not want to receive marketing emails from Flowcode.'
                    }
                  />
                )}
              />
            </Box>
          ) : (
            <ForgotPasswordLink onForgotPasswordClick={onForgotPasswordClick} />
          )}

          <VerticalSpacer mt="16px" />

          <AnimatedButton
            height="38px"
            width="100%"
            loading={isAuthenticating}
            loadingProps={{
              width: '20px',
              height: '20px',
              marginTop: '10px',
              label: isSignUp ? 'Signing up' : 'Signing in'
            }}
            type="submit"
            colorVariant="primary"
            sizeVariant="mobileFullWidth"
            data-testid="upgrade-auth-form-submit-btn"
            label={submitButtonLabel}
            fontWeight={600}
            onClick={onClickSubmitBtn}
            fontSize="16px"
          />
          {error && (
            <Text margin="12px 0" textAlign="center" variant="body/small" color="status.errorDark">
              {error}
            </Text>
          )}

          <VerticalSpacer mt="16px" />
          <FormToggleCTA isSignUp={isSignUp} onClick={onAuthFormToggle} />
          <VerticalSpacer mt="16px" />
          <Disclaimer />
          <VerticalSpacer mt="16px" />
        </form>
      </Box>
    </>
  )
}

export default UpgradeAuthForm
